import React, { useMemo } from 'react'

import { Month, getYearHeading } from '@utils/date-utils'

import { Select } from '@components/core/select'
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/core/select'

import { store } from '@store/index'

import { YearInfo } from 'types/filter'

export interface YearPickerProps {
  fiscalYearStart?: Month
  value: YearInfo
  maxAllowed?: YearInfo
  minAllowed?: YearInfo
  onChange: (year: YearInfo) => void
  render?: (year: YearInfo) => React.ReactNode
  suffix?: string
  yearsList?: YearInfo[]
}

export const getYears = (fiscalYear?: Month): YearInfo[] => {
  const currentYear = new Date().getFullYear()
  const state = store.getState()
  const earliestTransactionYear =
    state.auth.user?.business_earliest_transaction_year || currentYear - 1
  const latestTransactionYear = state.auth.user?.business_latest_transaction_year || currentYear

  // If the range is too large (more than 50 years), center it around the current year
  const maxRange = 50
  const totalRange = latestTransactionYear - earliestTransactionYear

  if (totalRange > maxRange) {
    const halfRange = Math.floor(maxRange / 2)
    const start = Math.max(earliestTransactionYear, currentYear - halfRange)
    const end = Math.min(latestTransactionYear, currentYear + halfRange)
    return new Array(end - start + 1).fill(0).map((_, idx) => ({ year: idx + start }))
  }

  return new Array(latestTransactionYear - earliestTransactionYear + 1)
    .fill(0)
    .map((_, idx) => ({ year: idx + earliestTransactionYear }))
}

const toValue = (info?: YearInfo) => (!info || !info.year ? undefined : `${info?.year}`)
const fromValue = (value: string) => ({ year: +value })

const outOfBounds = (info: YearInfo, minAllowed?: YearInfo, maxAllowed?: YearInfo) => {
  if (minAllowed && info.year < minAllowed.year) return true
  if (maxAllowed && info.year > maxAllowed.year) return true
  return false
}

export const YearPicker = React.memo((props: YearPickerProps) => {
  const { fiscalYearStart, value, onChange, render, maxAllowed, minAllowed, suffix, yearsList } =
    props

  // Memoize the years list to prevent unnecessary recalculations
  const years = useMemo(() => {
    return yearsList ?? getYears(fiscalYearStart)
  }, [yearsList, fiscalYearStart])

  // Memoize the filtered years to prevent unnecessary filtering on every render
  const filteredYears = useMemo(() => {
    return years.filter((yearInfo) => yearInfo.year !== null && yearInfo.year !== undefined)
  }, [years])

  return (
    <div className='w-auto'>
      <Select value={toValue(value)} onValueChange={(value) => onChange(fromValue(value))}>
        <SelectTrigger variant='outline' layout='inline'>
          <SelectValue placeholder='Select Year' className='w-auto' />
        </SelectTrigger>
        <SelectContent>
          {filteredYears.map((info) => (
            <SelectItem
              key={info.year}
              value={toValue(info) as string}
              disabled={outOfBounds(info, minAllowed, maxAllowed)}
              className='flex h-9 w-full justify-between rounded-none data-[state=checked]:bg-[#E9F0FD] data-[state=checked]:text-black'
            >
              {render
                ? render(info)
                : `${getYearHeading(info.year, fiscalYearStart)}${suffix ? ` ${suffix}` : ''}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
})
